import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, MapPin, Search, Megaphone, Share2, MousePointerClick, ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/launchx/Reveal";
import { agencyWa } from "./AgencyLayout";

const SERVICES = [
  {
    id: "website", icon: Globe, title: "Business Website",
    tagline: "A fast, mobile-first website that turns visitors into enquiries.",
    points: ["Professional design with your branding", "Click-to-WhatsApp and call buttons", "Enquiry form with instant alerts", "Hosting guidance and launch support"],
    wa: "I need a business website. Please share details.",
  },
  {
    id: "gbp", icon: MapPin, title: "Google Business Profile",
    tagline: "Show up when nearby customers search for what you sell.",
    points: ["Complete profile setup and verification guidance", "Categories, services, photos and posts", "Review strategy and responses", "Insights tracking — calls, directions, visits"],
    wa: "I want to set up / improve my Google Business Profile.",
  },
  {
    id: "seo", icon: Search, title: "Local SEO",
    tagline: "Rank higher on Google Search and Maps for your area.",
    points: ["Keyword research for your city and niche", "On-page optimisation of your website", "Local citations and map presence", "Monthly visibility and enquiry reporting"],
    wa: "I want my business to rank higher on Google locally.",
  },
  {
    id: "meta-ads", icon: Megaphone, title: "Meta Ads (Facebook & Instagram)",
    tagline: "Targeted ads that bring enquiries, not just reach.",
    points: ["Audience and creative strategy", "Lead-generation and WhatsApp campaigns", "Pixel setup and conversion tracking", "Weekly optimisation with clear reporting"],
    wa: "I want to run Meta ads for my business.",
  },
  {
    id: "google-ads", icon: MousePointerClick, title: "Google Ads",
    tagline: "Appear at the top when customers are ready to buy.",
    points: ["Search campaigns for buying-intent keywords", "Call and location extensions", "Conversion tracking from day one", "Budget controlled by you, always"],
    wa: "I want to run Google ads for my business.",
  },
  {
    id: "social", icon: Share2, title: "Social Media Setup & Management",
    tagline: "A professional presence that builds trust before the first call.",
    points: ["Business page/profile setup and optimisation", "Content calendar and branded creatives", "WhatsApp Business catalogue setup", "Enquiry routing and quick-reply systems"],
    wa: "I want professional social media management for my business.",
  },
];

export const AgencyServices = () => {
  const [active, setActive] = useState(SERVICES[0].id);
  const current = SERVICES.find((s) => s.id === active);

  return (
    <section id="services" data-testid="agency-services" className="bg-background py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600">Our Services</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Everything a local business needs to win online
          </h2>
          <p className="mt-4 text-base text-slate-600 max-w-2xl leading-relaxed">
            Pick one service or let us build your complete online presence. Every engagement starts with a free audit and ends with measurable enquiries.
          </p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[320px_1fr] gap-8">
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {SERVICES.map((s) => (
              <button key={s.id} data-testid={`agency-service-tab-${s.id}`} onClick={() => setActive(s.id)}
                className={`flex items-center gap-3 rounded-2xl px-5 py-4 text-left text-sm font-semibold whitespace-nowrap lg:whitespace-normal transition-all border ${
                  active === s.id ? "bg-navy text-white border-navy shadow-lg shadow-navy/20" : "bg-white text-slate-600 border-slate-200/80 hover:border-electric/40 hover:text-navy"
                }`}>
                <s.icon size={17} className={active === s.id ? "text-cyanx" : "text-electric"} /> {s.title}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={current.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}
              data-testid="agency-service-panel" className="bg-white border border-slate-200/80 rounded-3xl p-7 sm:p-10 shadow-sm">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-electric/10 flex items-center justify-center">
                    <current.icon size={22} className="text-electric" />
                  </div>
                  <h3 className="mt-4 font-display text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">{current.title}</h3>
                  <p className="mt-2 text-base text-slate-600 max-w-lg">{current.tagline}</p>
                </div>
                <a data-testid="agency-service-panel-whatsapp" href={agencyWa(current.wa)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-electric hover:bg-electric-dark text-white text-sm font-semibold rounded-full px-6 py-3 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-electric/30">
                  Enquire <ArrowRight size={15} />
                </a>
              </div>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                {current.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Check size={15} className="mt-0.5 shrink-0 text-electric" /> {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
